import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ConfigService } from '../../shared-services/config.service';
import { HttpClient } from '@angular/common/http';
import { GlobalDropdownComponent } from '../global-dropdown/global-dropdown.component';
import { MatSliderComponent } from '../mat-slider/mat-slider.component';
import {
  debounceTime,
  distinctUntilChanged,
  startWith,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '../button/button.component';
import saveAs from 'file-saver';
import { DateCalenderComponent } from '../date-calender/date-calender.component';
import { KeycloakService } from "keycloak-angular";
import { environment } from '../../../../../my-app/src/environments/environment.dev';

@Component({
  selector: 'app-global-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    GlobalDropdownComponent,
    MatSliderComponent,
    MatIcon,
    ButtonComponent,
    DateCalenderComponent,
  ],
  templateUrl: './global-filter.component.html',
  styleUrl: './global-filter.component.scss',
})
export class GlobalFilterComponent {
  @Input() config!: any;

  filters!: any[];
  options: Record<string, any[]> = {};
  filterForm: FormGroup = new FormGroup({});
  selectedKey = '';

  private defaultAppliedKeys = new Set<string>();
  private manualSelection: Set<string> = new Set();
  private autoPatchedKeys = new Set<string>();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private route: ActivatedRoute,
    private router: Router,
     private keycloakService: KeycloakService
  ) {this.initializeKeycloak();}
    token:any;
  private async initializeKeycloak() {
    this.token = await this.keycloakService.getToken();
    // console.log("Keycloak Token:", this.token);

    // Store or use the token as needed
  }

  report_url = environment.const_url_report;
  ngOnInit(): void {
    console.log("Token in filter:", this.token);

    const { apiUrl } = this.config;
    this.filters = this.config?.content || [];
    const queryParams = this.route?.snapshot?.queryParams ?? {};

    // Build form controls for all filters
    this.filters.forEach((item: any) => {
      this.filterForm.addControl(item?.key, new FormControl(null));
    });

    // Pre-fetch static filters (from assets) and apply defaults
    this.filters.forEach((filter, index) => {
      if (filter?.options === 'data' && filter?.apiUrl?.startsWith('assets/')) {
        this.http.get(filter?.apiUrl).subscribe((staticRes: any) => {
          const opts = staticRes?.data || [];
          this.filters[index].optionsList = opts;
          this.options[filter?.key] = opts;

          const control = this.filterForm.get(filter?.key);
          const hasQueryParam = !!queryParams?.[filter?.key];
          const currentVal = control?.value;
          const isEmpty =
            currentVal === null ||
            (Array.isArray(currentVal) && currentVal.length === 0);

          const shouldDefault =
            !hasQueryParam &&
            isEmpty &&
            opts.length > 0 &&
            !this.defaultAppliedKeys.has(filter.key) &&
            filter?.isfirstOptionDefault;

          if (shouldDefault) {
            const defaultValue = filter.isMultiSelect ? [opts[0]] : opts[0];
            control?.setValue(defaultValue, { emitEvent: false });

            // propagate to global filters too
            const arr = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
            this.globalFilterService.updateFilterSelection(
              filter?.key,
              arr,
              this.filters?.find((f: any) => f.key === filter?.key)?.isQueryParam || false
            );

            this.defaultAppliedKeys.add(filter?.key);
          }
        });
      }
    });

    // Manual selection flag for any special filter (example: customer_spec)
    this.filterForm.get('customer_spec')?.valueChanges.subscribe((val) => {
      if (val !== null && val !== undefined && val !== '') {
        this.autoPatchedKeys.delete('customer_spec');
      }
    });

    // Listen to value changes
    let valueChange$ = this.filterForm.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    );

    // If there are no query params, trigger once at start
    if (!Object.keys(queryParams).length) {
      valueChange$ = this.filterForm.valueChanges.pipe(
        startWith(this.filterForm.value),
        debounceTime(200),
        distinctUntilChanged((p, c) => JSON.stringify(p) === JSON.stringify(c))
      );
    }

    valueChange$.subscribe(() => {
      const payload = {
        filter: {
          where: this.getWhereCondition(),
          join: {},
          orderBy: {},
        },
      };

      this.configService.getDropdownOptions(apiUrl, payload , this.token).subscribe((res) => {
        console.log('Dropdown options response:', res);
        // update options for each filter
        this.filters = this.filters.map((filter: any) => {
          if (res.hasOwnProperty(filter.key)) {
            const opts = res[filter.key] ?? [];
            const control = this.filterForm.get(filter.key);
            const hasQueryParam = !!queryParams?.[filter.key];
            const currentVal = control?.value;
            const isEmpty =
              currentVal === null ||
              (Array.isArray(currentVal) && currentVal?.length === 0);

            // Apply default if needed
            if (opts?.length > 0 && !!filter?.isfirstOptionDefault) {
              if (isEmpty || this.autoPatchedKeys.has(filter?.key)) {
                const defaultValue = filter.isMultiSelect ? [opts[0]] : opts[0];
                control?.setValue(defaultValue, { emitEvent: true });

                const arr = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
                this.globalFilterService.updateFilterSelection(
                  filter?.key,
                  arr,
                  this.filters?.find((f: any) => f.key === filter?.key)?.isQueryParam || false
                );

                this.autoPatchedKeys.add(filter?.key);
              }

              // If query param present for that key, push currentVal to global filters once
              if (opts?.length && !this.autoPatchedKeys.has('customer_spec') && !!queryParams?.[filter?.key]) {
                const arr = Array.isArray(currentVal) ? currentVal : [currentVal];
                this.globalFilterService.updateFilterSelection(
                  filter.key,
                  arr,
                  this.filters.find((f: any) => f.key === filter.key)?.isQueryParam || false
                );
                this.autoPatchedKeys.add('customer_spec');
              }
            }

            // keep options on the filter
            this.options[filter.key] = opts;
            return { ...filter, optionsList: opts };
          }
          return filter;
        });

        // update shared options for filters not currently active
        if (!!res) {
          Object.entries(res).forEach(([key, value]) => {
            if (this.globalFilterService.activeFilter !== key) {
              this.options[key] = (value as any[]) ?? [];
            }
          });
        }
      });
    });

    // Apply query param values to the form / global filters once
    const paramKeyToFormKeyMap: Record<string, string> = {
      customer_spec: 'customer_spec',
    };

    if (Object.keys(queryParams).length) {
      Object.keys(queryParams).forEach((paramKey) => {
        const formKey = paramKeyToFormKeyMap[paramKey];
        const rawValue = queryParams[paramKey];

        if (formKey && this.filterForm.contains(formKey)) {
          const isMultiSelect = this.config.content.find(
            (f: any) => f.key === formKey
          )?.isMultiSelect;

          const valueArray = Array.isArray(rawValue)
            ? rawValue
            : typeof rawValue === 'string' && rawValue.includes(',')
            ? rawValue.split(',').map(decodeURIComponent)
            : isMultiSelect
            ? [rawValue]
            : rawValue;

          const patchValue = valueArray;

          this.filterForm.patchValue({ [formKey]: patchValue }, { emitEvent: true });

          const arr = Array.isArray(patchValue) ? patchValue : [patchValue];
          this.globalFilterService.updateFilterSelection(
            formKey,
            arr,
            this.filters?.find((elm: any) => elm.key === formKey)?.isQueryParam || false
          );
        }
      });
    } else {
      setTimeout(() => this.globalFilterService.initialTrigger(), 200);
    }
  }

  private getWhereCondition(): any[] {
    const currentPath = this.router.url.split('?')[0]; // Remove query params
    const dashboardName = currentPath.startsWith('/')
      ? currentPath.substring(1)
      : currentPath;

    let staticFilters: any[] = [];

    // Example static filter rules
    if (dashboardName === 'customer-overview') {
      staticFilters = [
        {
          attributeName: 'customer_name',
          operator: '!=',
          attributeValues: ['NA'],
          logicalOperator: 'AND',
        },
      ];
    } else if (dashboardName !== 'opportunity-overview') {
      staticFilters = [
        {
          attributeName: 'customer_name',
          operator: '!=',
          attributeValues: ['NA'],
          logicalOperator: 'AND',
        },
        {
          attributeName: 'latest_shop_order_flag',
          operator: '=',
          attributeValues: [true],
          logicalOperator: 'AND',
        },
      ];
    }

    // Dynamic filters from form (exclude queryParam ones)
    const dynamicFilters = this.config?.content
      .filter((elm: any) => {
        const val = this.filterForm.value[elm?.key];
        if (elm?.key === 'customer_spec') return false; // special case
        return (val !== null && val !== undefined && !elm?.isQueryParam && !this.autoPatchedKeys.has(elm?.key));
      })
      .map((elm: any) => {
        const rawVal = this.filterForm.value[elm.key];
        const values = Array.isArray(rawVal) ? rawVal.filter((v: any) => v !== null) : [rawVal];

        if (!values?.length || values.every((v) => v === null || v === '')) return null;

        return {
          attributeName: elm?.key,
          operator: values?.length > 1 ? 'IN' : '=',
          attributeValues: values,
          logicalOperator: 'AND',
        };
      })
      .filter(Boolean) as any[];

    // fix logicalOperator chaining
    if (dynamicFilters.length > 0) {
      dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
      if (staticFilters.length > 0) {
        staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
      }
    } else if (staticFilters.length > 0) {
      staticFilters[staticFilters.length - 1].logicalOperator = '';
    }

    return [...staticFilters, ...dynamicFilters];
  }

resetButton(): void {
  this.filterForm.reset();
  this.selectedKey = '';
  this.autoPatchedKeys.clear();
  this.manualSelection.clear();

  const defaultFilters = {
    queryParams: {},
    requestParams: {},
    widgetId: null,
  };

  this.globalFilterService.updateAllFilters(defaultFilters);

  // ✅ now that the service has it, clear the last export body
  this.globalFilterService.setExportPayload(null);
}

  onButtonSelectionChange(key: string): void {
    this.selectedKey = key;
  }

download(url: string): void {

  const final_url = this.report_url + url;
  console.log('Initiating download from URL:', final_url);
  const tablePayload = this.globalFilterService.getLatestExportPayload();

  const fallback = {
    filter: {
      where: this.getWhereCondition(),
      join: {},
      orderBy: {},
    },
  };

  const payload = tablePayload ?? fallback;

  this.globalFilterService.downloadExcel(final_url, payload , this.token).subscribe({
    next: (blob: Blob) => {
      console.log('Download response received');
      saveAs(blob, 'data.csv');
    },
    error: (err) => console.error('Download failed', err),
  });
}
}
